/**
 * Defines a custom element if it hasn't been defined yet.
 * @param {string} name - The name of the custom element.
 * @param {CustomElementConstructor} component - The custom element's constructor.
 * @returns {Function} The custom element's constructor.
 */
const defineComponent = (name, component) => {
  if (!customElements.get(name)) {
    customElements.define(name, component);
  }
  return component;
};

/**
 * Registers a custom element with specified behavior and lifecycle callbacks.
 * @param {string} name - The name of the custom element to define.
 * @param {function(HTMLElement): (void|(()=>void)|Promise<void|(()=>void)>)} c
 *        - A function that defines the behavior of the component. This function
 *        accepts an HTMLElement as a parameter and can return nothing, a cleanup
 *        function, or a promise that resolves to nothing or a cleanup function.
 */
export const component = (name, c) => {
  defineComponent(
    name,
    class extends HTMLElement {
      static formAssociated = true;
      constructor() {
        super();
        this.disconnect = undefined;
      }
      async connectedCallback() {
        const callback = await c(this);
        if (callback) {
          this.disconnect = callback;
        }
      }
      disconnectedCallback() {
        if (this.disconnect) {
          this.disconnect();
        }
      }
    }
  );
};

/**
 * Creates an HTML element from a template string.
 * @param {TemplateStringsArray} raw - The template strings.
 * @param {...(string|number)} exp - The values to interpolate into the template.
 * @returns {Element} The first element from the parsed HTML string.
 */
export const html = (raw, ...exp) =>
  new DOMParser().parseFromString(
    raw[0] + exp.map((item, index) => item + raw[index + 1]).join(""),
    "text/html"
  ).body.firstElementChild || new Element();
